import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { aboutTranslations } from '../translations/about';
import { teamTranslations } from '../translations/team';
import { contactTranslations } from '../translations/contact';
import { PageSection, SectionContent, SectionTitle, BlackButtonLink } from '../styles/SharedStyles';

const AboutContainer = styled.div`
  width: 100%;
  font-family: 'Montserrat', sans-serif;
`;

const Section = styled(PageSection)`
  &#aboutus {
    background-color: #f9f9f9;
  }
  
  &#team {
    background-color: white;
  }
  
  &#contact {
    background-color: #f9f9f9;
  }
`;

const SectionSubtitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  text-align: center;
  color: #444;
`;

const SectionText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #666;
  margin-bottom: 1.5rem;
  text-align: left;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const TeamMembersGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 4rem;
  max-width: 1000px;
  margin: 3rem auto 0;
`;

const TeamMemberCard = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  max-width: 450px;
`;

const TeamMemberImage = styled.div`
  width: 250px;
  height: 250px;
  border-radius: 50%;
  overflow: hidden;
  margin: 0 auto 1.5rem;
  background-image: url(${props => `/images/${props.image}`});
  background-size: cover;
  background-position: center;
`;

const TeamMemberName = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
`;

const TeamMemberRole = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 1rem;
`;

const TeamMemberDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #555;
  max-width: 350px;
  margin: 0 auto;
  text-align: justify;
  flex-grow: 1;
`;

const ContactInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  max-width: 800px;
  margin: 1.5rem auto 0;
`;

const ContactInfoSection = styled.div`
  text-align: center;
  max-width: 500px;
  width: 100%;
`;

const ContactInfoTitle = styled.h4`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #333;
`;

const ContactInfoText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #666;
  margin-bottom: 0.5rem;
`;

const About = () => {
  const { language } = useLanguage();
  const aboutT = aboutTranslations[language];
  const teamT = teamTranslations[language];
  const contactT = contactTranslations[language];
  
  return (
    <AboutContainer>
      {/* About Us Section */}
      <Section id="aboutus">
        <SectionContent>
          <SectionTitle>{aboutT.title}</SectionTitle>
          <SectionSubtitle>{aboutT.subtitle}</SectionSubtitle>
          <SectionText>
            {aboutT.paragraph1}
          </SectionText>
          <SectionText>
            {aboutT.paragraph2}
          </SectionText>
        </SectionContent>
      </Section>
      
      {/* Team Section */}
      <Section id="team">
        <SectionContent>
          <SectionTitle>{teamT.title}</SectionTitle>
          <SectionSubtitle>{teamT.subtitle}</SectionSubtitle>
          
          <TeamMembersGrid>
            {teamT.teamMembers.map(member => (
              <TeamMemberCard key={member.id}>
                <TeamMemberImage image={member.image} />
                <TeamMemberName>{member.name}</TeamMemberName>
                <TeamMemberRole>{member.role}</TeamMemberRole>
                <TeamMemberDescription>{member.description}</TeamMemberDescription>
              </TeamMemberCard>
            ))}
          </TeamMembersGrid>
        </SectionContent>
      </Section>
      
      {/* Contact Section */}
      <Section id="contact">
        <SectionContent>
          <SectionTitle>{contactT.title}</SectionTitle>
          <SectionSubtitle>{contactT.subtitle}</SectionSubtitle>
          
          <ContactInfoContainer>
            <ContactInfoSection>
              <BlackButtonLink href="https://calendly.com/plix-ai-consulting/meeting" target="_blank" rel="noopener noreferrer">
                {language === 'de' ? 'Kostenlose KI-Beratung' : 'Free AI Consultation'}
              </BlackButtonLink>
            </ContactInfoSection>
            
            <ContactInfoSection>
              <ContactInfoTitle>{contactT.companyDetailsTitle}</ContactInfoTitle>
              <ContactInfoText>
                Plix AI GmbH<br />
                Kolonnenstraße 8<br />
                Berlin<br />
                Germany<br />
                <br />
                {contactT.email}
              </ContactInfoText>
            </ContactInfoSection>
          </ContactInfoContainer>
        </SectionContent>
      </Section>
    </AboutContainer>
  );
};

export default About;
